import React, { useState, useMemo } from 'react';
import { Table, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TrackTableWrapper, TrackTableStyled, TableScrollWrapper, PaginationWrapper, PaginationButton, ItemsPerPageText, StatusBadge, SearchWrapper } from './style';
import { Filter } from 'components/Filter';

export interface TableColumn<T = any> {
  key: keyof T;
  title: string;
  render?: (value: any, item: T) => React.ReactNode;
}

export interface ServerModeConfig {
  totalPages: number;
  currentPage: number;
  onPageChange: (page: number) => void;
  searchTerm: string;
  onSearch: (term: string) => void;
  itemsPerPageText?: string;
}

export interface TrackTableProps<T = any> {
  data: T[];
  columns: TableColumn<T>[];
  itemsPerPage?: number;
  serverMode?: ServerModeConfig;
}

export const TrackTable = <T extends Record<string, any>>({ 
  data, 
  columns, 
  itemsPerPage = 10,
  serverMode
}: TrackTableProps<T>) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  
  const effectiveSearchTerm = serverMode ? serverMode.searchTerm : searchTerm;

  const filteredData = useMemo(() => {
    if (serverMode) return data;
    if (!effectiveSearchTerm.trim()) return data;
    
    return data.filter((item) => {
      return columns.some((column) => {
        const value = item[column.key];
        if (typeof value === 'string') {
          return value.toLowerCase().includes(effectiveSearchTerm.toLowerCase());
        }
        return String(value).toLowerCase().includes(effectiveSearchTerm.toLowerCase());
      });
    });
  }, [data, effectiveSearchTerm, columns, serverMode]);
  
  const totalPages = serverMode ? serverMode.totalPages : Math.ceil(filteredData.length / itemsPerPage);
  const effectiveCurrentPage = serverMode ? serverMode.currentPage : currentPage;
  const startIndex = (effectiveCurrentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentData = serverMode ? filteredData : filteredData.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    if (serverMode) {
      serverMode.onPageChange(page);
      return;
    }
    setCurrentPage(page);
  };

  const handleSearch = (term: string) => {
    if (serverMode) {
      serverMode.onSearch(term);
      return;
    }
    setSearchTerm(term);
    setCurrentPage(1); 
  };

  const renderPagination = () => {
    const pages: JSX.Element[] = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= 0) {
      return pages;
    }
    
    pages.push(
      <PaginationButton
        key={1}
        active={effectiveCurrentPage === 1}
        onClick={() => handlePageChange(1)}
      >
        1
      </PaginationButton>
    );

    const startPage = Math.max(2, effectiveCurrentPage - 1);
    const endPage = Math.min(totalPages - 1, effectiveCurrentPage + 1);

    if (startPage > 2) {
      pages.push(
        <PaginationButton key="ellipsis1" disabled>
          ...
        </PaginationButton>
      );
    }

    for (let i = startPage; i <= endPage; i++) {
      if (i !== 1 && i !== totalPages) {
        pages.push(
          <PaginationButton
            key={i}
            active={effectiveCurrentPage === i}
            onClick={() => handlePageChange(i)}
          >
            {i}
          </PaginationButton>
        );
      }
    }

    if (endPage < totalPages - 1) {
      pages.push(
        <PaginationButton key="ellipsis2" disabled>
          ...
        </PaginationButton>
      );
    }

    if (totalPages > 1) {
      pages.push(
        <PaginationButton
          key={totalPages}
          active={effectiveCurrentPage === totalPages}
          onClick={() => handlePageChange(totalPages)}
        >
          {totalPages}
        </PaginationButton>
      );
    }

    return pages;
  };

  return (
    <>
      <SearchWrapper>
        <Filter
          currentSearchTerm={effectiveSearchTerm}
          btnClick={(term: string) => handleSearch(term)}
          onFilterChange={(term: string) => handleSearch(term)}
        />
      </SearchWrapper>

      <TrackTableWrapper>
        <TableScrollWrapper>
        <TrackTableStyled>
          <thead>
            <tr>
              {columns.map((column) => (
                <th key={String(column.key)}>{column.title}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {currentData.map((item, index) => (
              <tr key={item.id || index}>
                {columns.map((column) => (
                  <td key={String(column.key)}>
                    {column.render 
                      ? column.render(item[column.key], item)
                      : item[column.key]
                    }
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </TrackTableStyled>
        </TableScrollWrapper>
      </TrackTableWrapper>
      
      <PaginationWrapper>
        <div className="pagination-buttons">
          <PaginationButton
            disabled={effectiveCurrentPage === 1}
            onClick={() => handlePageChange(effectiveCurrentPage - 1)}
          >
            <FontAwesomeIcon icon={['fas', 'chevron-left']} />
          </PaginationButton>
          
          {renderPagination()}
          
          <PaginationButton
            disabled={effectiveCurrentPage === totalPages}
            onClick={() => handlePageChange(effectiveCurrentPage + 1)}
          >
            <FontAwesomeIcon icon={['fas', 'chevron-right']} />
          </PaginationButton>
        </div>
        
        <ItemsPerPageText>
          {serverMode?.itemsPerPageText || `${itemsPerPage} itens por página`}
        </ItemsPerPageText>
      </PaginationWrapper>
    </>
  );
};
